import React, { useState, useEffect } from 'react';
import {
  RiUserLine,
  RiMailLine,
  RiPhoneLine,
  RiShieldUserLine,
  RiLockPasswordLine,
  RiEyeLine,
  RiEyeOffLine,
  RiSaveLine,
  RiKeyLine,
  RiCheckLine,
} from 'react-icons/ri';
import ApiWithLogging from '../utils/apiWithLogging';
import { useToast } from '../utils/toast';
import { getApiErrorMessage } from '../utils/mapFieldErrors';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import ConfirmModal from '../components/ui/ConfirmModal';
import Layout from '../components/ui/Layout';

interface UserProfile {
  id: number;
  username: string;
  email: string;
  full_name: string;
  phone: string | null;
  role_name: string;
  is_active: boolean;
}

interface ProfileFormData {
  full_name: string;
  phone: string;
}

interface PasswordFormData {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

const ProfilePage: React.FC = () => {
  const { showToast } = useToast();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const { showModal, onConfirmLeave, onCancelLeave } = useUnsavedChanges(isFormDirty);

  // Profile state
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileForm, setProfileForm] = useState<ProfileFormData>({
    full_name: '',
    phone: '',
  });

  // Password state
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordForm, setPasswordForm] = useState<PasswordFormData>({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<Partial<PasswordFormData>>({});

  // Load profile on mount
  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setProfileLoading(true);
    try {
      const data = await ApiWithLogging.get<UserProfile>('/users/me', {}, { service: 'profile', skipLogging: true });
      setProfile(data);
      setProfileForm({
        full_name: data.full_name || '',
        phone: data.phone || '',
      });
    } catch (err: any) {
      showToast('เกิดข้อผิดพลาด', 'ไม่สามารถโหลดข้อมูลผู้ใช้ได้', 'error');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileSaving(true);
    try {
      const updated = await ApiWithLogging.put<UserProfile>(
        '/users/me',
        {
          full_name: profileForm.full_name.trim(),
          phone: profileForm.phone.trim() || null,
        },
        {},
        { service: 'profile', skipLogging: true }
      );
      setProfile(updated);
      setProfileForm({
        full_name: updated.full_name || '',
        phone: updated.phone || '',
      });
      setIsFormDirty(false);
      showToast('สำเร็จ', 'บันทึกข้อมูลส่วนตัวเรียบร้อยแล้ว', 'success');
    } catch (err: any) {
      const msg = getApiErrorMessage(err, 'ไม่สามารถบันทึกข้อมูลได้');
      showToast('เกิดข้อผิดพลาด', msg, 'error');
    } finally {
      setProfileSaving(false);
    }
  };

  const validatePassword = (): boolean => {
    const errors: Partial<PasswordFormData> = {};

    if (!passwordForm.current_password) {
      errors.current_password = 'กรุณากรอกรหัสผ่านปัจจุบัน';
    }

    if (!passwordForm.new_password) {
      errors.new_password = 'กรุณากรอกรหัสผ่านใหม่';
    } else if (passwordForm.new_password.length < 8) {
      errors.new_password = 'รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร';
    } else if (!/[A-Z]/.test(passwordForm.new_password)) {
      errors.new_password = 'รหัสผ่านต้องมีตัวพิมพ์ใหญ่อย่างน้อย 1 ตัว';
    } else if (!/[a-z]/.test(passwordForm.new_password)) {
      errors.new_password = 'รหัสผ่านต้องมีตัวพิมพ์เล็กอย่างน้อย 1 ตัว';
    } else if (!/[0-9]/.test(passwordForm.new_password)) {
      errors.new_password = 'รหัสผ่านต้องมีตัวเลขอย่างน้อย 1 ตัว';
    }

    if (!passwordForm.confirm_password) {
      errors.confirm_password = 'กรุณายืนยันรหัสผ่านใหม่';
    } else if (passwordForm.new_password !== passwordForm.confirm_password) {
      errors.confirm_password = 'รหัสผ่านใหม่และยืนยันรหัสผ่านไม่ตรงกัน';
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePassword()) return;

    setPasswordSaving(true);
    try {
      await ApiWithLogging.post(
        '/users/me/password',
        {
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password,
        },
        {},
        { service: 'profile', skipLogging: true }
      );
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      setPasswordErrors({});
      showToast('สำเร็จ', 'เปลี่ยนรหัสผ่านเรียบร้อยแล้ว กรุณาเข้าสู่ระบบใหม่ด้วยรหัสผ่านใหม่', 'success');
      // BUG-S2001-002 (KD3 S2001, 2026-09-02): backend invalidate ทุก token รวมของ session นี้ทันทีที่เปลี่ยน
      // รหัสผ่าน (SEC FLOW-AUTH-018) — ถ้าปล่อยไว้ คลิกถัดไปจะโดนเด้งพร้อมข้อความ "เซสชันหมดอายุ" ซึ่งผิดบริบท
      // → ล้าง token ฝั่ง client แล้วพาไปหน้า login เอง (peer tabs จะเจอ 401 → forceLogout ตามปกติ)
      setTimeout(() => {
        ['access_token', 'refresh_token', 'token_expires_at', 'user', 'permissions'].forEach((k) => localStorage.removeItem(k));
        window.location.href = '/login';
      }, 2000);
    } catch (err: any) {
      // OBS-S457-001 fix: extract via KRP-envelope-aware helper (was reading .detail only)
      const detail = getApiErrorMessage(err, '');
      // KD3 S2533 (FLOW-AUTH-051): BE ตอบไทย "รหัสผ่านไม่ถูกต้อง" (BUSINESS_RULE_VIOLATION) ไม่ใช่ "incorrect"
      // → เงื่อนไขเดิมไม่เคยเข้า ผู้ใช้เห็นแค่ "รหัสผ่านไม่ถูกต้อง" ทั้งที่มี 3 ช่อง ไม่รู้ว่าช่องไหน
      if (detail && /incorrect|รหัสผ่านไม่ถูกต้อง/i.test(detail)) {
        showToast('เกิดข้อผิดพลาด', 'รหัสผ่านปัจจุบันไม่ถูกต้อง', 'error');
      } else {
        showToast('เกิดข้อผิดพลาด', detail || 'ไม่สามารถเปลี่ยนรหัสผ่านได้', 'error');
      }
    } finally {
      setPasswordSaving(false);
    }
  };

  // Password strength indicator
  const getPasswordStrength = (pwd: string): { label: string; color: string; width: string } => {
    if (!pwd) return { label: '', color: '', width: '0%' };
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[a-z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;

    if (score <= 2) return { label: 'อ่อนแอ', color: 'bg-danger-500', width: '33%' };
    if (score <= 4) return { label: 'ปานกลาง', color: 'bg-warning-500', width: '66%' };
    return { label: 'แข็งแกร่ง', color: 'bg-success-500', width: '100%' };
  };

  const strength = getPasswordStrength(passwordForm.new_password);

  if (profileLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto mb-3"></div>
            <p className="text-secondary-500 text-sm">กำลังโหลดข้อมูล...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
    <div className="max-w-2xl mx-auto space-y-6">
      <ConfirmModal
        isOpen={showModal}
        onClose={onCancelLeave}
        onConfirm={onConfirmLeave}
        pushHistoryOnOpen={false}
        title="ข้อมูลยังไม่ได้บันทึก"
        message="คุณมีข้อมูลที่ยังไม่ได้บันทึก หากออกจากหน้านี้การแก้ไขจะหายไป ต้องการออกจากหน้านี้หรือไม่?"
        confirmText="ออกจากหน้านี้"
        cancelText="อยู่ต่อ"
        isDanger={true}
      />
      {/* Page Header */}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm">
          <RiUserLine className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-secondary-900 leading-tight">ข้อมูลส่วนตัว</h1>
          <p className="text-xs text-secondary-500 mt-0.5">จัดการข้อมูลโปรไฟล์และรหัสผ่านของคุณ</p>
        </div>
      </div>

      {/* Section 1: User Info */}
      <div className="bg-white rounded-xl shadow-card border border-secondary-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-secondary-100 bg-secondary-50/50">
          <h2 className="text-base font-semibold text-secondary-900 flex items-center gap-2">
            <RiUserLine className="w-5 h-5 text-primary-600" />
            ข้อมูลผู้ใช้งาน
          </h2>
        </div>
        <form onSubmit={handleProfileSubmit} className="px-6 py-5 space-y-5">
          {/* Full Name */}
          <div>
            <label htmlFor="profile-full_name" className="block text-sm font-medium text-secondary-700 mb-1.5">
              ชื่อ-สกุล <span className="text-danger-500">*</span>
            </label>
            <div className="relative">
              <RiUserLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
              <input
                type="text"
                value={profileForm.full_name}
                onChange={(e) => { setProfileForm(prev => ({ ...prev, full_name: e.target.value })); setIsFormDirty(true); }}
                className="input pl-9 pr-4"
                id="profile-full_name"
                placeholder="กรอกชื่อ-สกุล"
                required
              />
            </div>
          </div>

          {/* Email (read-only) */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1.5">
              อีเมล
            </label>
            <div className="relative">
              <RiMailLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
              <input
                type="email"
                aria-label="อีเมล"
                value={profile?.email || ''}
                readOnly
                className="input pl-9 pr-4 bg-secondary-50 text-secondary-500 cursor-not-allowed"
              />
            </div>
            <p className="text-xs text-secondary-600 mt-1">อีเมลไม่สามารถเปลี่ยนแปลงได้</p>
          </div>

          {/* Username (read-only) */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1.5">
              ชื่อผู้ใช้งาน (Username)
            </label>
            <div className="relative">
              <RiShieldUserLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
              <input
                type="text"
                aria-label="ชื่อผู้ใช้งาน"
                value={profile?.username || ''}
                readOnly
                className="input pl-9 pr-4 bg-secondary-50 text-secondary-500 cursor-not-allowed"
              />
            </div>
            <p className="text-xs text-secondary-600 mt-1">ชื่อผู้ใช้งานไม่สามารถเปลี่ยนแปลงได้</p>
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="profile-phone" className="block text-sm font-medium text-secondary-700 mb-1.5">
              เบอร์โทรศัพท์
            </label>
            <div className="relative">
              <RiPhoneLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
              <input
                type="tel"
                value={profileForm.phone}
                onChange={(e) => { setProfileForm(prev => ({ ...prev, phone: e.target.value })); setIsFormDirty(true); }}
                className="input pl-9 pr-4"
                id="profile-phone"
                placeholder="กรอกเบอร์โทรศัพท์"
              />
            </div>
          </div>

          {/* Role (read-only) */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1.5">
              บทบาท
            </label>
            <div className="relative">
              <RiShieldUserLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
              <input
                type="text"
                aria-label="บทบาท"
                value={profile?.role_name || ''}
                readOnly
                className="input pl-9 pr-4 bg-secondary-50 text-secondary-500 cursor-not-allowed"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={profileSaving}
              className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {profileSaving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <RiSaveLine className="w-4 h-4" />
              )}
              {profileSaving ? 'กำลังบันทึก...' : 'บันทึกการเปลี่ยนแปลง'}
            </button>
          </div>
        </form>
      </div>

      {/* Section 2: Change Password */}
      <div className="bg-white rounded-xl shadow-card border border-secondary-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-secondary-100 bg-secondary-50/50">
          <h2 className="text-base font-semibold text-secondary-900 flex items-center gap-2">
            <RiLockPasswordLine className="w-5 h-5 text-primary-600" />
            เปลี่ยนรหัสผ่าน
          </h2>
        </div>
        <form onSubmit={handlePasswordSubmit} className="px-6 py-5 space-y-5">
          {/* Current Password */}
          <div>
            <label htmlFor="profile-current_password" className="block text-sm font-medium text-secondary-700 mb-1.5">
              รหัสผ่านปัจจุบัน <span className="text-danger-500">*</span>
            </label>
            <div className="relative">
              <RiKeyLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                value={passwordForm.current_password}
                onChange={(e) => {
                  setPasswordForm(prev => ({ ...prev, current_password: e.target.value }));
                  if (passwordErrors.current_password) {
                    setPasswordErrors(prev => ({ ...prev, current_password: undefined }));
                  }
                }}
                className={`input pl-9 pr-11 ${passwordErrors.current_password ? 'border-danger-400 bg-danger-50' : ''}`}
                id="profile-current_password"
                placeholder="กรอกรหัสผ่านปัจจุบัน"
                maxLength={128}
                aria-invalid={Boolean(passwordErrors.current_password)}
                aria-describedby={passwordErrors.current_password ? 'current_password-error' : undefined}
              />
              <button
                type="button"
                aria-label={showCurrentPassword ? 'ซ่อนรหัสผ่านปัจจุบัน' : 'แสดงรหัสผ่านปัจจุบัน'}
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-secondary-400 hover:text-secondary-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/30"
              >
                {showCurrentPassword ? <RiEyeOffLine className="w-4 h-4" /> : <RiEyeLine className="w-4 h-4" />}
              </button>
            </div>
            {passwordErrors.current_password && (
              <p id="current_password-error" className="text-xs text-danger-600 mt-1">{passwordErrors.current_password}</p>
            )}
          </div>

          {/* New Password */}
          <div>
            <label htmlFor="profile-new_password" className="block text-sm font-medium text-secondary-700 mb-1.5">
              รหัสผ่านใหม่ <span className="text-danger-500">*</span>
            </label>
            <div className="relative">
              <RiLockPasswordLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={passwordForm.new_password}
                onChange={(e) => {
                  setPasswordForm(prev => ({ ...prev, new_password: e.target.value }));
                  if (passwordErrors.new_password) {
                    setPasswordErrors(prev => ({ ...prev, new_password: undefined }));
                  }
                }}
                className={`input pl-9 pr-11 ${passwordErrors.new_password ? 'border-danger-400 bg-danger-50' : ''}`}
                id="profile-new_password"
                placeholder="กรอกรหัสผ่านใหม่"
                maxLength={128}
                aria-invalid={Boolean(passwordErrors.new_password)}
                aria-describedby={passwordErrors.new_password ? 'new_password-error' : undefined}
              />
              <button
                type="button"
                aria-label={showNewPassword ? 'ซ่อนรหัสผ่านใหม่' : 'แสดงรหัสผ่านใหม่'}
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-secondary-400 hover:text-secondary-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/30"
              >
                {showNewPassword ? <RiEyeOffLine className="w-4 h-4" /> : <RiEyeLine className="w-4 h-4" />}
              </button>
            </div>
            {passwordErrors.new_password && (
              <p id="new_password-error" className="text-xs text-danger-600 mt-1">{passwordErrors.new_password}</p>
            )}
            {/* Password strength */}
            {passwordForm.new_password && !passwordErrors.new_password && (
              <div className="mt-2">
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-secondary-200 rounded-full h-1.5 overflow-hidden">
                    <div
                      className={`h-1.5 rounded-full transition-all duration-300 ${strength.color}`}
                      style={{ width: strength.width }}
                    />
                  </div>
                  <span className={`text-xs font-medium min-w-[3.5rem] text-right ${
                    strength.label === 'อ่อนแอ' ? 'text-danger-600' :
                    strength.label === 'ปานกลาง' ? 'text-warning-600' :
                    strength.label === 'แข็งแกร่ง' ? 'text-success-600' : 'text-secondary-500'
                  }`}>{strength.label}</span>
                </div>
              </div>
            )}
            {/* Password requirements hint */}
            <p className="text-xs text-secondary-600 mt-1.5">
              ต้องมีอย่างน้อย 8 ตัวอักษร ประกอบด้วยตัวพิมพ์ใหญ่ ตัวพิมพ์เล็ก และตัวเลข
            </p>
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="profile-confirm_password" className="block text-sm font-medium text-secondary-700 mb-1.5">
              ยืนยันรหัสผ่านใหม่ <span className="text-danger-500">*</span>
            </label>
            <div className="relative">
              <RiLockPasswordLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={passwordForm.confirm_password}
                onChange={(e) => {
                  setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }));
                  if (passwordErrors.confirm_password) {
                    setPasswordErrors(prev => ({ ...prev, confirm_password: undefined }));
                  }
                }}
                className={`input pl-9 pr-11 ${passwordErrors.confirm_password ? 'border-danger-400 bg-danger-50' : ''}`}
                id="profile-confirm_password"
                placeholder="กรอกรหัสผ่านใหม่อีกครั้ง"
                maxLength={128}
                aria-invalid={Boolean(passwordErrors.confirm_password)}
                aria-describedby={passwordErrors.confirm_password ? 'confirm_password-error' : undefined}
              />
              <button
                type="button"
                aria-label={showConfirmPassword ? 'ซ่อนรหัสผ่านยืนยัน' : 'แสดงรหัสผ่านยืนยัน'}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-secondary-400 hover:text-secondary-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/30"
              >
                {showConfirmPassword ? <RiEyeOffLine className="w-4 h-4" /> : <RiEyeLine className="w-4 h-4" />}
              </button>
            </div>
            {passwordErrors.confirm_password && (
              <p id="confirm_password-error" className="text-xs text-danger-600 mt-1">{passwordErrors.confirm_password}</p>
            )}
            {/* Match indicator */}
            {passwordForm.confirm_password && passwordForm.new_password && !passwordErrors.confirm_password && (
              <p className="text-xs text-success-700 mt-1 flex items-center gap-1">
                <RiCheckLine className="w-3.5 h-3.5" /> รหัสผ่านตรงกัน
              </p>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={passwordSaving}
              className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {passwordSaving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <RiKeyLine className="w-4 h-4" />
              )}
              {passwordSaving ? 'กำลังเปลี่ยน...' : 'เปลี่ยนรหัสผ่าน'}
            </button>
          </div>
        </form>
      </div>
    </div>
    </Layout>
  );
};

export default ProfilePage;
